<script setup lang="ts">
import { computed, ref } from "vue";
import { type ActiveElement, type ChartEvent } from "chart.js";
import BaseChart from "./BaseChart.vue";

export type BaseTrendChartSeriesType = "line" | "bar";
export type BaseTrendChartAxisPosition = "left" | "right";

export interface BaseTrendChartPoint {
    id: string;
    label: string;
}

export interface BaseTrendChartSeries {
    id: string;
    label: string;
    values: number[];
    type?: BaseTrendChartSeriesType;
    axisId?: string;
    color?: string;
    fill?: boolean;
}

export interface BaseTrendChartAxis {
    id: string;
    position?: BaseTrendChartAxisPosition;
    beginAtZero?: boolean;
}

export type BaseTrendChartValueFormatter = (value: number, series: BaseTrendChartSeries, point: BaseTrendChartPoint) => string;
export type BaseTrendChartAxisFormatter = (value: number, axisId: string) => string;
export type BaseTrendChartLabelFormatter = (label: string, point: BaseTrendChartPoint) => string;

const props = withDefaults(defineProps<{
    points: BaseTrendChartPoint[];
    series: BaseTrendChartSeries[];
    axes?: BaseTrendChartAxis[];
    theme?: string;
    height?: string;
    ariaLabel?: string;
    labelFormatter?: BaseTrendChartLabelFormatter;
    valueFormatter?: BaseTrendChartValueFormatter;
    axisValueFormatter?: BaseTrendChartAxisFormatter;
    selectedPointId?: string | null;
}>(), {
    axes: () => [],
    theme: "",
    height: "100%",
    ariaLabel: "Trend chart",
    labelFormatter: (label: string) => label,
    valueFormatter: (value: number) => String(value),
    axisValueFormatter: (value: number) => String(value),
    selectedPointId: null,
});

const emit = defineEmits<{
    (event: "update:selectedPointId", value: string | null): void;
    (event: "point-hover", point: BaseTrendChartPoint | null): void;
    (event: "point-select", point: BaseTrendChartPoint): void;
}>();

const chartHostRef = ref<HTMLDivElement | null>(null);

const resolvedAxes = computed<BaseTrendChartAxis[]>(() => {
    if (props.axes.length) return props.axes;
    const ids = [...new Set(props.series.map((series) => series.axisId || "primary"))];
    return ids.map((id, index) => ({ id, position: index === 0 ? "left" : "right", beginAtZero: true }));
});

function resolveColor(value: string | undefined, fallbackToken: string, fallback: string): string {
    const requestedVariable = value?.match(/^var\((--[A-Za-z0-9_-]+)\)$/)?.[1];
    const token = requestedVariable || fallbackToken;
    const host = chartHostRef.value ?? document.documentElement;
    return getComputedStyle(host).getPropertyValue(token).trim() || fallback;
}

function withAlpha(color: string, alpha: number): string {
    if (color.startsWith("#")) {
        const hex = color.slice(1);
        const expanded = hex.length === 3 ? hex.split("").map((part) => part + part).join("") : hex;
        const red = Number.parseInt(expanded.slice(0, 2), 16);
        const green = Number.parseInt(expanded.slice(2, 4), 16);
        const blue = Number.parseInt(expanded.slice(4, 6), 16);
        if ([red, green, blue].every(Number.isFinite)) return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
    }
    return color;
}

function colorForSeries(series: BaseTrendChartSeries, index: number): string {
    const fallbacks = [
        ["--pn-selection-ring", "#22d3ee"],
        ["--pn-success", "#22c55e"],
        ["--pn-warning", "#fbbf24"],
        ["--pn-danger", "#f87171"],
    ] as const;
    const [token, fallback] = fallbacks[index % fallbacks.length]!;
    return resolveColor(series.color, token, fallback);
}

function pointAt(index: number): BaseTrendChartPoint | null {
    return props.points[index] ?? null;
}

function activeIndex(elements: ActiveElement[]): number | null {
    const index = elements[0]?.index;
    return typeof index === "number" ? index : null;
}

function updateHover(elements: ActiveElement[]): void {
    const point = pointAt(activeIndex(elements) ?? -1);
    emit("point-hover", point);
}

function handleChartClick(_event: ChartEvent, elements: ActiveElement[]): void {
    const point = pointAt(activeIndex(elements) ?? -1);
    if (!point) return;
    emit("update:selectedPointId", point.id);
    emit("point-select", point);
}

const chartData = computed(() => {
    const selectedIndex = props.points.findIndex((point) => point.id === props.selectedPointId);
    return {
        labels: props.points.map((point) => props.labelFormatter(point.label, point)),
        datasets: props.series.map((series, index) => {
            const type = series.type ?? "line";
            const color = colorForSeries(series, index);
            const values = props.points.map((_, pointIndex) => series.values[pointIndex] ?? 0);
            const selectedColors = values.map((_, pointIndex) => selectedIndex === pointIndex ? color : withAlpha(color, type === "bar" ? 0.42 : 1));
            return {
                type,
                label: series.label,
                data: values,
                yAxisID: series.axisId || resolvedAxes.value[0]?.id || "primary",
                backgroundColor: type === "bar" ? selectedColors : color,
                borderColor: color,
                borderWidth: type === "bar" ? 1 : 2,
                borderRadius: type === "bar" ? 3 : undefined,
                hoverBackgroundColor: color,
                hoverBorderColor: color,
                hoverBorderWidth: 2,
                pointBackgroundColor: color,
                pointBorderColor: color,
                pointRadius: type === "line" ? values.map((_, pointIndex) => selectedIndex === pointIndex ? 6 : 3) : undefined,
                pointHoverRadius: type === "line" ? 7 : undefined,
                tension: type === "line" ? 0.2 : undefined,
                fill: series.fill ?? false,
            };
        }),
    };
});

const chartOptions = computed(() => {
    void props.theme;
    const textColor = resolveColor("", "--pn-text-muted", "#94a3b8");
    const surface = resolveColor("", "--pn-surface-elevated", "#172033");
    const text = resolveColor("", "--pn-text", "#f8fafc");
    return {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        onHover: (_event: ChartEvent, elements: ActiveElement[]) => updateHover(elements),
        onClick: handleChartClick,
        plugins: {
            legend: { display: false },
            tooltip: {
                enabled: true,
                mode: "index",
                intersect: false,
                backgroundColor: surface,
                titleColor: text,
                bodyColor: text,
                borderColor: resolveColor("", "--pn-border-strong", "#475569"),
                borderWidth: 1,
                callbacks: {
                    title: (items: Array<{ dataIndex?: number }>) => {
                        const point = pointAt(items[0]?.dataIndex ?? -1);
                        return point ? props.labelFormatter(point.label, point) : "";
                    },
                    label: (context: { dataIndex: number; datasetIndex: number }) => {
                        const point = pointAt(context.dataIndex);
                        const series = props.series[context.datasetIndex];
                        if (!point || !series) return "";
                        return `${series.label}: ${props.valueFormatter(series.values[context.dataIndex] ?? 0, series, point)}`;
                    },
                },
            },
        },
        scales: resolvedAxes.value.reduce<Record<string, unknown>>((result, axis, index) => {
            result[axis.id] = {
                type: "linear",
                position: axis.position ?? (index === 0 ? "left" : "right"),
                beginAtZero: axis.beginAtZero ?? true,
                grid: { color: index === 0 ? resolveColor("", "--pn-border", "#334155") : "transparent", drawOnChartArea: index === 0 },
                ticks: { color: textColor, callback: (value: string | number) => props.axisValueFormatter(Number(value), axis.id) },
            };
            return result;
        }, {
            x: {
                ticks: { color: textColor, maxRotation: 0, autoSkip: true, maxTicksLimit: 8 },
                grid: { display: false },
            },
        }),
    };
});
</script>

<template>
    <div ref="chartHostRef" :style="{ height, minHeight: '12rem', position: 'relative', width: '100%' }" :aria-label="ariaLabel" role="img">
        <BaseChart type="bar" :data="chartData" :options="chartOptions" height="100%" />
    </div>
</template>
